import firebase from 'firebase/app';
import 'firebase/firestore';

const text = value => typeof value === 'string' ? value.trim() : '';
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const validDocId = value => typeof value === 'string' && value === value.trim() &&
  value.length > 0 && !value.includes('/') && value !== '.' && value !== '..';

function passengerRefs(db, passengerId, passengerSessionId, now) {
  if (!db || typeof db.collection !== 'function' || typeof db.batch !== 'function' ||
      !validDocId(passengerId) || !validDocId(passengerSessionId) ||
      !/^\d+(\.\d+)?$/.test(passengerSessionId) ||
      !Number.isFinite(Number(passengerSessionId)) || Number(passengerSessionId) <= 0 ||
      typeof now !== 'number' || !Number.isFinite(now) || now < 0) {
    throw new TypeError('A valid passenger, session, database, and time are required.');
  }
  const passenger = db.collection('Passengers').doc(passengerId);
  return { passenger, session: passenger.collection('sessions').doc(passengerSessionId) };
}

// recheck: the rider is in the presence lock grace (fresh position sent, driver verdict pending).
export async function initializePassengerVisit(db, {
  passengerId, passengerSessionId, driverUid, driverSessionStart, locationGranted, hasProximity,
  recheck = false,
} = {}, now = Date.now()) {
  if (!validDocId(driverUid) || typeof driverSessionStart !== 'number' ||
      !Number.isFinite(driverSessionStart) || driverSessionStart <= 0 ||
      typeof locationGranted !== 'boolean' || typeof hasProximity !== 'boolean' ||
      typeof recheck !== 'boolean') {
    throw new TypeError('A valid driver session and Boolean location state are required.');
  }
  const { passenger, session } = passengerRefs(db, passengerId, passengerSessionId, now);
  const [passengerSnapshot, sessionSnapshot] = await Promise.all([passenger.get(), session.get()]);
  const { arrayUnion, serverTimestamp } = firebase.firestore.FieldValue;
  const action = hasProximity
    ? (sessionSnapshot.exists ? 'refreshed_queue_view' : 'START_PAX_SESSION')
    : recheck ? 'geofence_recheck' : 'START_geofence_locked';
  const batch = db.batch();
  batch.set(passenger, {
    pax_id: passengerId,
    strive_drivers: arrayUnion(driverUid),
    sessions: arrayUnion(passengerSessionId),
    striver_sessions: arrayUnion(driverSessionStart),
    last_used: serverTimestamp(),
    ...(!passengerSnapshot.exists ? { last_started: Number(passengerSessionId) } : {}),
  }, { merge: true });
  batch.set(session, {
    potential_pax_id: passengerId,
    driver_uid: driverUid,
    striver_session_id: driverSessionStart,
    geo_accepted: locationGranted,
    session_id: Number(passengerSessionId),
    ...(!sessionSnapshot.exists ? { time_accessed: serverTimestamp() } : {}),
    // Union even for a new visit: another auth/queue write may have arrived
    // after the existence read. Never replace that action history.
    actions: arrayUnion(action),
    interactions: arrayUnion({ action, time: now / 1000 }),
  }, { merge: true });
  await batch.commit();
}

export async function savePassengerSpotify(db, {
  passengerId, passengerSessionId, profile, accessToken, refreshToken,
} = {}, now = Date.now()) {
  const access = text(accessToken);
  const refresh = text(refreshToken);
  const placeholders = ['NO_ACCESS', 'NO_REFRESH'];
  if (!access || !refresh || placeholders.includes(access) || placeholders.includes(refresh) ||
      !isRecord(profile) || !text(profile.id) || !text(profile.name)) {
    // Never include token/profile values in validation errors or logs.
    throw new TypeError('A verified Spotify profile and real access and refresh tokens are required.');
  }
  const optionalProfile = {};
  for (const key of ['email', 'uri', 'image']) {
    if (profile[key] != null && typeof profile[key] !== 'string') {
      throw new TypeError('Optional Spotify profile values must be strings.');
    }
    // Explicitly clear unavailable provider values when a passenger connects a
    // different account; merge must not retain the previous account's metadata.
    optionalProfile[`spotify_${key}`] = text(profile[key]);
  }
  const { passenger, session } = passengerRefs(db, passengerId, passengerSessionId, now);
  const { arrayUnion, serverTimestamp } = firebase.firestore.FieldValue;
  const spotify = { spotify_access: access, spotify_refresh: refresh, spotify_name: text(profile.name) };
  const batch = db.batch();
  batch.set(passenger, {
    ...spotify,
    spotify_id: text(profile.id),
    ...optionalProfile,
    last_used: serverTimestamp(),
  }, { merge: true });
  batch.set(session, {
    potential_pax_id: passengerId,
    actions: arrayUnion('SPOTIFY_AUTH_SUCCESS'),
    interactions: arrayUnion({ action: 'SPOTIFY_AUTH_SUCCESS', meta: spotify, time: now / 1000 }),
  }, { merge: true });
  await batch.commit();
}
